import {
  Icon,
  PencilOutlineIcon,
  TrashOutlineIcon,
} from '@fountain/ripple/icon';
import {
  Chip,
  IconButton,
  Switch,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { CatalogAttribute, CatalogField } from './conditionForm';
import messages from './messages';
import { useStyles } from './styles';
import { SubjectTypeChip } from './SubjectTypeChip';
import { combineTriggers, triggerSummary, triggerText } from './triggerSummary';
import { ApprovalRule, ApprovalRuleLevel } from './types';

export interface RuleRowProps {
  rule: ApprovalRule;
  /** Field labels for the Trigger column; empty until the catalog resolves. */
  catalogFields: CatalogField[];
  /** Attribute labels for the Trigger column; empty until the catalog resolves. */
  catalogAttributes: CatalogAttribute[];
  /** Opens the editor. `level` is the 1-based level card to open it on. */
  onEdit?: (level?: number) => void;
  onToggle?: (enabled: boolean) => void;
  onDelete?: () => void;
  /** True while this row's own toggle request is in flight. */
  toggling?: boolean;
}

/**
 * The approvers who can settle a level, as the names an admin picked.
 *
 * A count answered "how many people" and never "who", which is the question a
 * list of gates is scanned for. A role approver has no name until submission
 * resolves it, so it shows the role instead — those are two different kinds of
 * approver and collapsing them into one number hid that too.
 */
const approverNames = (level: ApprovalRuleLevel | undefined) =>
  (level?.approvers ?? [])
    .map((approver, index) => ({
      // `user_id` is on the wire and was being thrown away here. Keyed by name
      // alone, two approvers sharing a display name — or a role whose name
      // equals a user's — gave React duplicate keys. The index is the fallback
      // for a role approver, which carries no id of its own; within one level the
      // list is stable, so position is a real identity there.
      key: approver.user_id ?? `${approver.role_name ?? ''}-${index}`,
      name: approver.user_name ?? approver.role_name ?? '',
    }))
    .filter(approver => approver.name.length > 0);

interface LevelRowProps {
  level: ApprovalRuleLevel;
  /** 1-based position in the sequence, which is what "Level 2" means. */
  number: number;
  catalogFields: CatalogField[];
  catalogAttributes: CatalogAttribute[];
  /** Dims with the rule, because a level of an off rule is not running either. */
  disabled: boolean;
  /** Ends the branch line here, so it does not run on into the next rule. */
  isLast: boolean;
  onEdit?: (level?: number) => void;
}

/**
 * One level of a multi-level rule.
 *
 * Carries a trigger and approvers of its own and nothing else — no toggle, no
 * edit, no delete. That is the locked decision on AXHE-4470: individual levels
 * cannot be switched on and off, and a level is not a record that can be deleted
 * from a list, only a card removed inside the rule it belongs to.
 */
const LevelRow: React.FC<LevelRowProps> = ({
  level,
  number,
  catalogFields,
  catalogAttributes,
  disabled,
  isLast,
  onEdit,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const trigger = triggerSummary({
    conditions: level.conditions,
    logic: level.condition_logic,
    scope: 'level',
    fields: catalogFields,
    attributes: catalogAttributes,
    intl,
  });
  const approvers = approverNames(level);
  const label = level.name
    ? intl.formatMessage(messages.levelRowNamed, { number, name: level.name })
    : intl.formatMessage(messages.levelRow, { number });

  return (
    <TableRow
      className={classNames(styles.levelRow, {
        [styles.ruleDisabled]: disabled,
        [styles.rowClickable]: Boolean(onEdit),
      })}
      data-testid="approval-rule-level"
      onClick={onEdit && (() => onEdit(number))}
    >
      <TableCell
        className={classNames(styles.cell, styles.levelCell, {
          [styles.levelCellLast]: isLast,
        })}
      >
        {onEdit ? (
          // The keyboard path to this level's card. Named for the level rather
          // than the rule: this control scrolls the panel to one card, and a
          // label saying "Edit rule" on four rows that each open somewhere
          // different would make the difference invisible to a screen reader.
          //
          // The accessible name *contains* the visible label rather than
          // replacing it — WCAG 2.5.3. "Edit level 2" against a row reading
          // "Level 2 · Finance sign-off" left a speech-input user with nothing
          // to say: the words they can see were not in the name they could
          // address. The unnamed case only looked fine because "Level 2" happens
          // to fall inside "Edit level 2".
          <button
            type="button"
            className={styles.nameButton}
            aria-label={intl.formatMessage(messages.editLevel, {
              level: label,
            })}
            onClick={event => {
              event.stopPropagation();
              onEdit(number);
            }}
          >
            {label}
          </button>
        ) : (
          <Typography variant="body2">{label}</Typography>
        )}
      </TableCell>

      {/* A level has no type of its own — it inherits the rule's, one row up. */}
      <TableCell className={styles.cell} />

      <TableCell className={classNames(styles.cell, styles.triggerCell)}>
        <Typography variant="body2" data-testid="level-trigger">
          {triggerText(trigger, intl)}
        </Typography>
      </TableCell>

      <TableCell className={styles.cell}>
        <div className={styles.approvers}>
          {approvers.map(approver => (
            <Chip
              key={approver.key}
              size="small"
              className={styles.approverChip}
              label={approver.name}
            />
          ))}
        </div>
      </TableCell>

      {/* The Enabled column. A level has no switch — AXHE-4470 locks that to
          whole rules — but "off" is still a state, and this is the column that
          reports state. The badge sat among the approver chips until review on
          #4599: navigating column-wise a screen reader announced
          "Approvers: Off", and a sighted reader found a state badge under a
          header promising people.

          Only reachable for levels switched off before the toggle was removed —
          and a level cannot be switched back on, so the ones that exist are
          permanent. The 45% opacity this replaces was the state's only tell;
          opacity survives neither a screenshot, a screen reader, nor a
          colourblind reader. */}
      <TableCell className={styles.cell}>
        {!level.enabled && (
          <Chip
            size="small"
            label={intl.formatMessage(messages.disabledLevel)}
            data-testid="level-off"
          />
        )}
      </TableCell>

      {/* No edit and no delete: a level is not a record that can be removed from
          a list, only a card removed inside the rule it belongs to. */}
      <TableCell className={classNames(styles.cell, styles.actionsCell)} />
    </TableRow>
  );
};

/**
 * One rule in the table, plus a nested row per level when it has more than one.
 *
 * A fragment of `<tr>`s rather than a wrapper element: a rule and its levels are
 * sibling rows of one table, so the branch line between them is drawn in the
 * level row's first cell. Wrapping them in a `<tbody>` each would give the table
 * one body per rule, and column widths are resolved per body — so every rule
 * would size its columns independently and no column would line up down the page.
 *
 * A single-level rule renders its level inline: the level adds no information the
 * rule row does not already carry, so a child row would be a disclosure step that
 * reveals nothing. A multi-level rule nests, because the order of levels is the
 * rule's actual behaviour — level 2 does not open until level 1 is satisfied —
 * and one flat summary cannot express that.
 */
export const RuleRow: React.FC<RuleRowProps> = ({
  rule,
  catalogFields,
  catalogAttributes,
  onEdit,
  onToggle,
  onDelete,
  toggling = false,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const levels = rule.levels ?? [];
  const isMultiLevel = levels.length > 1;
  // Not a permission — the account's admins may edit rules, this particular rule
  // is owned by the legacy backfill and would have its changes overwritten. An
  // action that is unavailable because of what the *record* is, rather than who
  // the user is, is not shown at all; the badge and hint already say why.
  const isEditable = !rule.managed_by_legacy_group;

  const scopeTrigger = triggerSummary({
    conditions: rule.scope_conditions,
    logic: rule.scope_condition_logic,
    scope: 'rule',
    subjectType: rule.subject_type,
    fields: catalogFields,
    attributes: catalogAttributes,
    intl,
  });

  // A single-level rule shows only this one row, so its cell has to carry both
  // gates: the scope decides whether the rule applies, the level decides whether
  // its only step runs, and `RuleMatcher` requires both — so the trigger is the
  // conjunction of the two. A scope that read "All offers" while the level
  // narrowed it to a position hid the condition that actually fires the gate. A
  // multi-level rule needs none of this: each level has its own row and trigger
  // below, so the scope alone is the right thing inline.
  const inlineLevel = isMultiLevel ? undefined : levels[0];
  const trigger =
    inlineLevel && (inlineLevel.conditions?.length ?? 0) > 0
      ? combineTriggers(
          scopeTrigger,
          triggerSummary({
            conditions: inlineLevel.conditions,
            logic: inlineLevel.condition_logic,
            scope: 'level',
            fields: catalogFields,
            attributes: catalogAttributes,
            intl,
          }),
          intl,
        )
      : scopeTrigger;

  // Only the single-level case. Each level of a multi-level rule has its own
  // approvers and its own row below saying so, and the rounds are sequential
  // rather than a pool — so pooling them here would name a set of people who
  // never approve together. The empty cell is a parent row in a tree, not
  // missing data: the chips are on the rows the branch line connects.
  const inlineApprovers = isMultiLevel ? [] : approverNames(levels[0]);

  const openEditor = isEditable && onEdit ? onEdit : undefined;

  return (
    <>
      <TableRow
        className={classNames(styles.rule, {
          [styles.ruleDisabled]: !rule.enabled,
          [styles.rowClickable]: Boolean(openEditor),
        })}
        data-testid="approval-rule"
        // A mouse convenience on top of the name button, not the row's
        // accessible affordance. `role="button"` here would make every control
        // inside the row a nested interactive element — an axe violation, and
        // the cause of Enter on the delete button both confirming and navigating
        // when `TemplateRow` tried it. The keyboard path is the name button, so
        // the row carries no `tabIndex` and no role of its own.
        onClick={openEditor && (() => openEditor())}
      >
        <TableCell className={styles.cell}>
          <div className={styles.ruleName}>
            {openEditor ? (
              <button
                type="button"
                className={styles.nameButton}
                // Stops the row's own handler from opening the panel a second
                // time; harmless today because both do the same thing, and the
                // kind of coincidence that stops being harmless.
                onClick={event => {
                  event.stopPropagation();
                  openEditor();
                }}
              >
                {rule.name}
              </button>
            ) : (
              <Typography variant="body2">{rule.name}</Typography>
            )}

            {/* Text, not colour alone — an "Off" rule has to read as off in a
                screenshot, a screen reader, and at 50% opacity. */}
            {!rule.enabled && (
              <Chip
                size="small"
                label={intl.formatMessage(messages.disabledRule)}
              />
            )}

            {rule.managed_by_legacy_group && (
              <Chip
                size="small"
                label={intl.formatMessage(messages.managedElsewhere)}
                data-testid="managed-elsewhere"
              />
            )}
          </div>

          {/* Under the name rather than in a column of its own: it describes the
              shape of the rule, and the rows below are where the levels
              themselves are read. */}
          {isMultiLevel && (
            <Typography
              variant="body2"
              color="textSecondary"
              className={styles.ruleMeta}
            >
              <FormattedMessage
                {...messages.levelCount}
                values={{ count: rule.level_count ?? levels.length }}
              />
            </Typography>
          )}

          {/* The badge says a rule is managed elsewhere; this says what to do
              about it. A badge on its own leaves the admin to guess why the row
              has no actions — and the consequence of guessing wrong (editing it
              on the old page vs. giving up) is the whole point of surfacing
              it. */}
          {rule.managed_by_legacy_group && (
            <Typography
              variant="body2"
              color="textSecondary"
              className={styles.ruleMeta}
            >
              <FormattedMessage {...messages.managedElsewhereHint} />
            </Typography>
          )}
        </TableCell>

        <TableCell className={styles.cell}>
          <SubjectTypeChip subjectType={rule.subject_type} testId="rule-type" />
        </TableCell>

        <TableCell className={classNames(styles.cell, styles.triggerCell)}>
          <Typography variant="body2" data-testid="rule-trigger">
            {triggerText(trigger, intl)}
          </Typography>
        </TableCell>

        <TableCell className={styles.cell}>
          <div className={styles.approvers} data-testid="rule-approvers">
            {inlineApprovers.map(approver => (
              <Chip
                key={approver.key}
                size="small"
                className={styles.approverChip}
                label={approver.name}
              />
            ))}
          </div>
        </TableCell>

        {/* The rule's toggle, and only the rule's. Design Requirements §2 locks
            "only whole rules have an enable/disable toggle" (AXHE-4470), so a
            level row's switch does not exist — but its *state* still reports
            here, which is why the inlined level's badge sits in this cell.
            Together they read as one statement: the rule is on, its only level
            is off. Split across two columns they read as a contradiction. */}
        <TableCell className={styles.cell}>
          <div className={styles.stateGroup}>
            {isEditable && onToggle && (
              <Switch
                checked={Boolean(rule.enabled)}
                disabled={toggling}
                onClick={event => event.stopPropagation()}
                onChange={event => onToggle(event.target.checked)}
                inputProps={{
                  'aria-label': `${intl.formatMessage(messages.ruleActiveToggle)} — ${rule.name}`,
                }}
              />
            )}

            {/* This matters *more* on an inlined level than on a nested one.
                `RuleMatcher` skips disabled levels and a rule with no active
                levels gates nothing — so a single-level rule whose only level is
                off shows a switch reading on while gating nothing at all.
                Inlining the level is a display choice; it must not also hide the
                one piece of its state the row cannot otherwise express. */}
            {!isMultiLevel && levels.length === 1 && !levels[0].enabled && (
              <Chip
                size="small"
                label={intl.formatMessage(messages.disabledLevel)}
                data-testid="level-off"
              />
            )}
          </div>
        </TableCell>

        <TableCell className={classNames(styles.cell, styles.actionsCell)}>
          {isEditable && (
            <>
              {onEdit && (
                <IconButton
                  size="small"
                  className={styles.rowAction}
                  aria-label={`${intl.formatMessage(messages.editRule)} — ${rule.name}`}
                  onClick={event => {
                    event.stopPropagation();
                    onEdit();
                  }}
                >
                  <Icon icon={PencilOutlineIcon} size="sm" color="inherit" />
                </IconButton>
              )}

              {/* Last, and never filled: destructive actions stay tertiary
                  weight so the row's primary affordance is editing. */}
              {onDelete && (
                <IconButton
                  size="small"
                  className={classNames(styles.rowAction, styles.dangerButton)}
                  aria-label={`${intl.formatMessage(messages.deleteRule)} — ${rule.name}`}
                  onClick={event => {
                    event.stopPropagation();
                    onDelete();
                  }}
                >
                  <Icon icon={TrashOutlineIcon} size="xs" color="inherit" />
                </IconButton>
              )}
            </>
          )}
        </TableCell>
      </TableRow>

      {isMultiLevel &&
        levels.map((level, index) => (
          <LevelRow
            // Levels have no client-visible id and are ordered, so position is
            // the identity here — `position` when the server sent it, falling
            // back to array index for a rule that has not been saved yet.
            key={level.position ?? index}
            catalogFields={catalogFields}
            catalogAttributes={catalogAttributes}
            disabled={!rule.enabled}
            isLast={index === levels.length - 1}
            level={level}
            number={index + 1}
            onEdit={openEditor}
          />
        ))}
    </>
  );
};
